import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable
} from 'firebase/storage';
import { firebaseConfig, HKEnvironment } from 'core/constants';
import { getEnvName, getCurrentUser, getIdToken } from 'core/util';
import { Capacitor } from '@capacitor/core';
import {
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  Auth
} from 'firebase/auth';
import { getApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import { initializeFirestore, getDoc, doc } from 'firebase/firestore';
import { BuilderLogo } from 'core/types';
import { sanitizeFileName } from 'core/util/uploadValidation';
// import { RemoteConfig, getRemoteConfig } from 'firebase/remote-config';

export const firebaseApp = initializeApp(firebaseConfig);
export const db = initializeFirestore(firebaseApp, {
  experimentalForceLongPolling: true
});

// Centralized Firebase Auth helper to prevent duplicate initialization
let authInstance: Auth | null = null;

export const getFirebaseAuth = (): Auth => {
  if (authInstance) {
    return authInstance;
  }

  try {
    if (Capacitor.isNativePlatform()) {
      authInstance = initializeAuth(getApp(), {
        persistence: indexedDBLocalPersistence
      });
    } else {
      authInstance = getAuth();
    }
  } catch (error: any) {
    // If already initialized (auth/already-initialized error), just get the existing instance
    if (error?.code === 'auth/already-initialized') {
      authInstance = getAuth();
    } else {
      throw error;
    }
  }

  return authInstance;
};
// export let remoteConfig: RemoteConfig = getRemoteConfig();
// Default fetch is once every 12 hours
// Rapidly fetches remoteConfig changes for development (only uncomment in devl)
// remoteConfig.settings.minimumFetchIntervalMillis = 10000;

const refreshFirebaseToken = async () => {
  getCurrentUser().then((result) => {
    if (!!result.user) {
      getIdToken();
    }
  });
};

export function useFirebaseUpload() {
  // Firebase app is already initialized above, no need to initialize again
  const uploadFile = async (
    homeId: number,
    name: string,
    file: any,
    contentType?: string
  ) => {
    await refreshFirebaseToken();
    const env = getEnvName() === HKEnvironment.PROD ? 'prod' : 'dev';
    const storage = getStorage();
    const safeName = sanitizeFileName(name);
    const storageRef = ref(storage, `/${env}/homes/home_${homeId}/${safeName}`);
    const blobFile = new Blob([file]);
    const metadata = { contentType: contentType ?? 'application/octet-stream' };
    return uploadBytes(storageRef, blobFile, metadata);
  };
  return {
    uploadFile
  };
}

export function useFirebaseUploadResumable() {
  // Firebase app is already initialized above, no need to initialize again
  const uploadFileResumable = (
    homeId: number,
    name: string,
    file: any,
    options?: { signal?: AbortSignal; contentType?: string }
  ) => {
    refreshFirebaseToken();
    const env = getEnvName() === HKEnvironment.PROD ? 'prod' : 'dev';
    const storage = getStorage();
    const safeName = sanitizeFileName(name);
    const storageRef = ref(storage, `/${env}/homes/home_${homeId}/${safeName}`);
    const blobFile = new Blob([file]);
    const metadata = {
      contentType: options?.contentType ?? 'application/octet-stream'
    };
    const uploadTask = uploadBytesResumable(storageRef, blobFile, metadata);

    // If an AbortSignal is provided, listen for the abort event and cancel the upload
    if (options?.signal) {
      options.signal.addEventListener('abort', () => {
        uploadTask.cancel();
      });
    }
    return uploadTask;
  };
  return {
    uploadFileResumable
  };
}

export const getFirebaseDownloadUrl = async (filePath: string) => {
  const storage = getStorage();
  const result = await getDownloadURL(ref(storage, `/${filePath}`));
  return result;
};

export const getFirebaseBuilderLogo = async (
  builderId: number
): Promise<BuilderLogo | undefined> => {
  try {
    const docRef = doc(db, 'builders', `builder_${builderId}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as BuilderLogo;
    }
    throw new Error('Document does not exist');
  } catch (error) {
    console.log(error);
  }
};
